import React, { useState, useEffect } from "react";
import { LinkImageIcon } from "../../../assets/icons/SvgIcons";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
} from "lexical";
import { CustomLinkNode } from "./CustomLinkNode";
import { toolTipConstants } from "../LexicalEditor.types";

const LinkImage = ({ editor }: { editor: LexicalEditor }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("current");
  const [hasCustomText, setHasCustomText] = useState(false);

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!hasCustomText) {
      setText(url);
    }
  }, [url, hasCustomText]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'k') {
        e.preventDefault();
      
        setIsModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    setHasCustomText(false);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
    setHasCustomText(true);
  };

  const handleSave = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        if (url) {
          const linkNode = new CustomLinkNode(
            url,
            target === "new" ? "_blank" : "_self",
            target === "new" ? "noopener noreferrer" : null
          );

          if (text) {
            const textNode = $createTextNode(text);
            linkNode.append(textNode);
          } else {
            const defaultTextNode = $createTextNode("Link");
            linkNode.append(defaultTextNode);
          }

          selection.insertNodes([linkNode]);
        } else if (text) {
          const textNode = $createTextNode(text);
          selection.insertNodes([textNode]);
        }
      }
    });

    setUrl("");
    setText("");
    setTitle("");
    setTarget("current");
    setIsModalOpen(false);
  };

  return (
    <div>
      <div
        onClick={toggleModal}
        style={{ cursor: "pointer" }}
        aria-label="Open link modal"
        className={`lexical-toolbarItem`}
        title={toolTipConstants.linkImage}
      >
        <LinkImageIcon />
      </div>

      {isModalOpen && (
        <div className="lexical-modal-overlay" role="dialog" aria-modal="true">
          <div className="lexical-modal-content">
            <div className="lexical-modal-header">
              <h3>Insert/Edit Link</h3>
              <button
                onClick={toggleModal}
                className="lexical-close-button"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="lexical-modal-body">
              <label className="lexical-label">
                <span className="lexical-label--text">URL</span>
                <input
                  type="text"
                  className="lexical-input"
                  value={url}
                  onChange={handleUrlChange}
                  aria-label="Link URL"
                />
              </label>
              <label className="lexical-label">
                <span className="lexical-label--text">Text to Display</span>
                <input
                  type="text"
                  className="lexical-input"
                  value={text}
                  onChange={handleTextChange}
                  aria-label="Text to Display"
                />
              </label>
              <label className="lexical-label">
                <span className="lexical-label--text">Title</span>
                <input
                  type="text"
                  className="lexical-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  aria-label="Link Title"
                />
              </label>
              <label className="lexical-label">
                <span className="lexical-label--text">Open Link In</span>
                <select
                  className="lexical-select"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  aria-label="Open Link In"
                >
                  <option value="current">Current Window</option>
                  <option value="new">New Window</option>
                </select>
              </label>
            </div>
            <div className="lexical-modal-footer">
              <button onClick={toggleModal} className="lexical-cancel-button">
                Cancel
              </button>
              <button className="lexical-save-button" onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkImage;
